// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Redux } from 'src/store'

// ** Types
import { GygOrdersType, GygOrderStateType, GygOrderType } from 'src/types/apps/gygOrderTypes'

// ** API Actions
import {
  GET_ORDER_BY_ORDER_ID_ACTION,
  GET_ORDER_BY_USER_ID_ACTION,
  ORDER_SLICE_NAME,
  REFUND_PAYMENT_ACTION
} from './actions'
import { GygPaymentRefundRequest, GygPaymentRefundResponse } from '@/src/types/apps/gygPaymentTypes'

// ** Async Thunks
interface OrderByUserIdResponse {
  data: GygOrdersType[]
}

interface OrderByOrderIdResponse {
  data: GygOrderType
}

export const refundPayment = createAsyncThunk<GygPaymentRefundResponse, GygPaymentRefundRequest, Redux>(
  REFUND_PAYMENT_ACTION,
  async (refundPaymentRequest: GygPaymentRefundRequest, { extra, rejectWithValue }) => {
    try {
      // Backend API expects amount and refundAmount as strings
      const response = await extra.httpClient.post(`/payments/refund`, {
        amount: refundPaymentRequest.amount?.toString() ?? '',
        paymentId: refundPaymentRequest.paymentId,
        refundAmount: refundPaymentRequest.refundAmount.toString(),
        refundReason: refundPaymentRequest.refundReason
      })

      return response.data
    } catch (error: any) {
      console.error(`Error refunding payment for paymentId: ${refundPaymentRequest.paymentId}`, error)

      // Construct a consistent error response structure
      if (error.response) {
        const { title, statusCode, message } = error.response.data

        return rejectWithValue({ title, statusCode, message } as GygPaymentRefundResponse)
      }

      // Handle unexpected errors
      return rejectWithValue({
        title: 'UnexpectedError',
        statusCode: 500,
        message: 'An unexpected error occurred.'
      })
    }
  }
)

export const getOrdersByUserId = createAsyncThunk<OrderByUserIdResponse, string, Redux>(
  GET_ORDER_BY_USER_ID_ACTION,
  async (userId: string, { extra }) => {
    const response = await extra.httpClient(`/orders?userId=${userId}`)

    return response.data
  }
)

export const getOrdersByOrderId = createAsyncThunk<OrderByOrderIdResponse, string, Redux>(
  GET_ORDER_BY_ORDER_ID_ACTION,
  async (orderId: string, { extra }) => {
    try {
      const response = await extra.httpClient(`/orders/${orderId}`)

      return response.data
    } catch (error) {
      // console.error(`Error fetching order details for orderId: ${orderId}`, error)
      throw error
    }
  }
)

// ** Initial State
const initialState: GygOrderStateType = {
  gygOrders: [],
  errors: [],
  pending: false,
  refundPending: false, // Add this for refund-specific loading state
  refundSuccess: false, // Add success state for refund
  order: null,
  getOrderPending: false
}

// ** Slice
export const gygOrdersSlice = createSlice({
  name: ORDER_SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Handling getOrdersByUserId actions
    builder
      .addCase(getOrdersByUserId.pending, state => {
        state.pending = true
      })
      .addCase(getOrdersByUserId.fulfilled, (state, action) => {
        state.pending = false
        state.gygOrders = action.payload.data
      })
      .addCase(getOrdersByUserId.rejected, state => {
        state.pending = false
      })

    // Handling getOrdersByOrderId actions
    builder
      .addCase(getOrdersByOrderId.pending, state => {
        state.getOrderPending = true
      })
      .addCase(getOrdersByOrderId.fulfilled, (state, action) => {
        state.getOrderPending = false
        state.order = action.payload.data as GygOrderType
      })
      .addCase(getOrdersByOrderId.rejected, state => {
        state.getOrderPending = false
      })

    // Handling refundPayment actions
    builder
      .addCase(refundPayment.pending, state => {
        state.refundPending = true // Use a separate `refundPending` flag if needed
        state.refundSuccess = false // Reset success state
        state.errors = [] // Clear previous errors
      })
      .addCase(refundPayment.fulfilled, state => {
        state.refundPending = false
        state.refundSuccess = true // Set success state
      })
      .addCase(refundPayment.rejected, (state, action) => {
        state.refundPending = false
        state.refundSuccess = false // Reset success state
        // Optionally, add the error message to `state.errors`
        if (action.payload && typeof action.payload === 'object' && 'message' in action.payload) {
          state.errors.push(action.payload.message as string)
        }
      })
  }
})

// ** Reducer
export default gygOrdersSlice.reducer
